import { SiteLocale } from 'constants/i18n.constants';
import { Routes } from 'constants/routes.constants';
import { CtaPageFieldFragment } from 'types/api/dato-operations.types';

import { LinkProps } from 'components/@common/Link/types';

import { FRONTEND_DOMAIN, isProd, isStaging } from './env.utils';

// Limit the amount of prebuild pages depending on the environment
const SLICE_END = isProd ? undefined : isStaging ? 10 : 1;

export const generateStaticPaths = <T extends object>(
  pages: Array<T>,
  locales: Array<string> | undefined,
  generator: (model: T) => string | undefined | null,
) => {
  const paths = pages.reduce<Array<string>>((acc, page) => {
    locales?.forEach((locale) => {
      const path = generator(page);
      if (path) {
        acc.push(`/${locale}${path}`);
      }
    });
    return acc;
  }, []);

  return [...new Set(paths)].slice(0, SLICE_END);
};

/**
 * replaces variables inside a route string so you don't have to manually build it
 * e.g. "/users/:id" > replacePathParams("/users/:id", { id: 1 }) > "/users/1"
 */
export const replacePathParams = (
  route: string,
  params: { [key: string]: string | number | null | undefined },
) => {
  let result = route;
  Object.keys(params).forEach((p) => {
    const value = params[p];
    if (value) {
      result = result.replace(`:${p}`, value.toString());
    }
  });
  return result;
};

// Everything that has the following 3 props is routable using urlFor or hrefFor
interface CtaProps {
  isExternal: boolean | undefined;
  url: string | undefined;
  page: Array<CtaPageFieldFragment>;
}

export const urlFor = (
  route: string | Array<CtaProps> | CtaProps,
  params: { [key: string]: string | number | null | undefined } = {},
) => {
  const uri =
    typeof route === 'string'
      ? replacePathParams(route, params)
      : hrefFor(route).href;
  // Ensure we don't have a trailing slash
  return `${FRONTEND_DOMAIN}${uri}`.replace(/\/$/, '');
};

const flattenCta = (cta: Array<CtaProps> | CtaProps) => {
  const ctaCollection = Array.isArray(cta) ? cta : [cta];
  return ctaCollection[0];
};

// Generate href tag and target / rel tags for CTA and navigation items
// Resolves pages using legacyPagerouteHref (falling back to path)
const hrefFor = (ctaArray?: Array<CtaProps> | CtaProps) => {
  if (!ctaArray) return { href: '#', target: '_self' };
  const cta = flattenCta(ctaArray);
  const href = (cta.isExternal ? cta.url : pageRouteFor(cta.page?.[0])) ?? '#';
  return cta.isExternal
    ? {
        href,
        target: '_blank',
        rel: 'noreferrer noopener',
      }
    : { href, target: '_self' };
};

const pageRouteFor = (
  page: CtaPageFieldFragment | undefined,
): string | undefined => {
  if (!page) return;
  if (!page.entity) return;

  const params = 'slug' in page ? page.slug : undefined;
  const routeFunc = PageRoutesMapping[page.entity];
  return routeFunc ? routeFunc(params) : undefined;
};

export const isExternalLink = (href?: LinkProps['href'] | null) =>
  typeof href === 'string' &&
  (href.indexOf('http') === 0 || href.indexOf('mailto:') === 0);

export const PageRoutesMapping: Record<
  string,
  (...slug: Array<string | undefined>) => string
> = {
  PageHomeRecord: () => Routes.Home,
  PageEventRecord: (slug) => replacePathParams(Routes.EventDetail, { slug }),
  PageCityRecord: (slug) => replacePathParams(Routes.CityDetail, { slug }),
  PageCollectionRecord: (slug) =>
    replacePathParams(Routes.CollectionDetail, { slug }),
  PageRecord: (slug) => replacePathParams(Routes.PageDetail, { slug }),
  PagePrivacyPolicyRecord: () => Routes.PrivacyPolicy,
  PageTermsOfServiceRecord: () => Routes.TermsOfService,
  TicketOfferRecord: (slug, code) =>
    replacePathParams(Routes.EventTicketOffer, { slug, code }),
  LoyaltyTierRecord: () => Routes.Account,
  LoyaltyRecord: () => Routes.Account,
};

// Add locale prefix to a route, this can be handy when the links will be used in emails (authentication),
// this ensures users get back to the correct locale
export const addLocalePrefix = (
  locale: SiteLocale = SiteLocale.EN,
  route: string,
  params?: { [key: string]: string | number | null | undefined },
) => {
  const prefixedRoute =
    locale === SiteLocale.AR ? `/${SiteLocale.AR}${route}` : route;

  if (params) return replacePathParams(prefixedRoute, params);
  return prefixedRoute;
};
